import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Modal, Pressable } from 'react-native';
import { useRoute, useNavigation, useFocusEffect, RouteProp, NavigationProp } from '@react-navigation/native';
import Svg, { Circle } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { PageContainer, Navbar, Breadcrumb, ActivityCalendar, Toast } from '../../components';
import TalentsSkeleton from './components/TalentsSkeleton';
import { talentTeachersApi, talentTeacherProjectLinksApi, talentActivitiesApi, talentSpecializationsApi, talentProjectsApi } from '../../services/apiService';
import { CalendarEvent } from '../../components/ActivityCalendar/types';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { FONTS } from '../../constants/config';
import { createShadow } from '../../utils/styles';
import { getStatusColor } from '../../components/charts/chartStyles';
import {
  TeacherDetailData,
  StudentSpecialization,
  SpecializationProgram,
  TalentActivityData,
} from './types';

const FOREGROUND = '#414042';

type ScreenRouteProp = RouteProp<MainStackParamList, 'TeacherDetail' | 'CoordinatorProfile'>;
type ScreenNavProp = NavigationProp<MainStackParamList>;

/* ── Teacher Header ─────────────────────────────────────────── */
function TeacherHeader({ data, onEdit, onRemove, readOnly, entityName }: { data: TeacherDetailData; onEdit: () => void; onRemove: () => void; readOnly?: boolean; entityName?: string }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <View style={[headerStyles.container, createShadow(0, 1, 3, '#000', 0.1)]}>
      <View style={headerStyles.row}>
        {/* Buttons - left side (hidden for strategy team) */}
        {!readOnly && (
          <View style={headerStyles.actionsRow}>
            <TouchableOpacity
              style={[headerStyles.actionBtn, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onEdit}
            >
              <Text style={headerStyles.actionText}>
                {isRTL ? 'تعديل البيانات' : 'Edit Data'}
              </Text>
              <Ionicons name="create-outline" size={14} color={FOREGROUND} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[headerStyles.actionBtn, createShadow(0, 1, 3, '#000', 0.1)]}
              onPress={onRemove}
            >
              <Text style={headerStyles.actionText}>
                {isRTL ? 'إزالة من القائمة' : 'Remove from List'}
              </Text>
              <Ionicons name="person-remove-outline" size={14} color={FOREGROUND} />
            </TouchableOpacity>
          </View>
        )}
        {/* Name - right side */}
        <View style={headerStyles.nameCol}>
          <Text style={headerStyles.name}>{data.name}</Text>
          <Text style={headerStyles.subtitle}>
            {data.school} - {data.specialization}
          </Text>
          {entityName && (
            <View style={headerStyles.entityTag}>
              <Text style={headerStyles.entityTagText}>{entityName}</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

/* ── Info Card Row ──────────────────────────────────────────── */
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={infoStyles.row}>
      <Text style={infoStyles.valueText}>{value || '-'}</Text>
      <View style={infoStyles.labelPill}>
        <Text style={infoStyles.labelText}>{label}</Text>
      </View>
    </View>
  );
}

function InfoCards({ data }: { data: TeacherDetailData }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const genderDisplay = data.gender === 'male' ? (isRTL ? 'ذكر' : 'Male') : data.gender === 'female' ? (isRTL ? 'أنثى' : 'Female') : data.gender;

  return (
    <View style={infoStyles.cardsRow}>
      {/* Left: Profile Info (الملف التعريفي) */}
      <View style={[infoStyles.card, createShadow(0, 1, 3, '#000', 0.1)]}>
        <Text style={infoStyles.cardTitle}>
          {isRTL ? 'الملف التعريفي' : 'Profile'}
        </Text>
        <InfoRow label={isRTL ? 'المدرسة' : 'School'} value={data.school} />
        <InfoRow label={isRTL ? 'المواهب' : 'Specializations'} value={data.specialization} />
        <InfoRow label={isRTL ? 'عدد المشاريع' : 'Projects'} value={String(data.projectCount)} />
        <InfoRow label={isRTL ? 'عدد الأنشطة' : 'Activities'} value={String(data.activityCount)} />
      </View>

      {/* Right: Personal Info (المعلومات الشخصية) */}
      <View style={[infoStyles.card, createShadow(0, 1, 3, '#000', 0.1)]}>
        <Text style={infoStyles.cardTitle}>
          {isRTL ? 'المعلومات الشخصية' : 'Personal Information'}
        </Text>
        <InfoRow label={isRTL ? 'الاسم' : 'Name'} value={data.name} />
        <InfoRow label={isRTL ? 'رقم الهوية' : 'Emirates ID'} value={data.eid ? data.eid.replace(/(\d{3})(\d{4})(\d{7})(\d)/, '$1-$2-$3-$4') : '-'} />
        <InfoRow label={isRTL ? 'البريد الالكتروني' : 'Email'} value={data.email} />
        <InfoRow label={isRTL ? 'الهاتف' : 'Phone'} value={data.phone} />
        <InfoRow label={isRTL ? 'تاريخ الميلاد' : 'Date of Birth'} value={data.birthDate} />
        <InfoRow label={isRTL ? 'الجنس' : 'Gender'} value={genderDisplay} />
      </View>
    </View>
  );
}

/* ── Specialization Card (with donut) ────────────────────────── */
function SpecializationDonut({ percentage }: { percentage: number }) {
  const size = 64;
  const strokeWidth = 10;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const progressColor = getStatusColor(percentage);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ position: 'absolute' }}>
        <Circle cx={center} cy={center} r={radius} stroke="#E5E7EB" strokeWidth={strokeWidth} fill="none" />
        <Circle
          cx={center} cy={center} r={radius}
          stroke={progressColor} strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          rotation={-90}
          origin={`${center}, ${center}`}
        />
      </Svg>
      <Text style={{ fontSize: 12, fontWeight: '700', color: FOREGROUND }}>%{percentage}</Text>
    </View>
  );
}

function SpecializationCard({ spec, onView, onRemove, readOnly }: { spec: StudentSpecialization; onView?: () => void; onRemove?: () => void; readOnly?: boolean }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View style={specCardStyles.container}>
      <View style={specCardStyles.headerRow}>
        <View style={specCardStyles.nameCol}>
          <Text style={specCardStyles.name}>{spec.title}</Text>
        </View>
        <SpecializationDonut percentage={spec.progress} />
      </View>
      <View style={specCardStyles.statsRow}>
        <View style={specCardStyles.statItem}>
          <Text style={specCardStyles.statLabel}>{isRTL ? 'المؤشرات' : 'KPIs'}</Text>
          <Text style={specCardStyles.statValue}>{spec.kpiCount}</Text>
        </View>
        <View style={specCardStyles.statItem}>
          <Text style={specCardStyles.statLabel}>{isRTL ? 'الأنشطة' : 'Activities'}</Text>
          <Text style={specCardStyles.statValue}>{spec.activityCount}</Text>
        </View>
        <View style={specCardStyles.statItem}>
          <Text style={specCardStyles.statLabel}>{isRTL ? 'المشاركين' : 'Participants'}</Text>
          <Text style={specCardStyles.statValue}>{spec.participantCount}</Text>
        </View>
      </View>
      <View style={specCardStyles.footerRow}>
        <View>
          <TouchableOpacity onPress={() => setMenuOpen(!menuOpen)}>
            <Ionicons name="ellipsis-vertical" size={18} color={FOREGROUND} />
          </TouchableOpacity>
          {menuOpen && (
            <View style={specCardStyles.dropdownMenu}>
              <TouchableOpacity
                style={specCardStyles.dropdownItem}
                onPress={() => { setMenuOpen(false); onRemove?.(); }}
              >
                <Text style={specCardStyles.dropdownItemText}>
                  {isRTL ? 'إزالة الموهبة' : 'Remove Specialization'}
                </Text>
                <Ionicons name="close-circle-outline" size={16} color="#DC2626" />
              </TouchableOpacity>
            </View>
            )}
          </View>
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={specCardStyles.linkRow} onPress={onView}>
          <Ionicons name="chevron-back" size={14} color={FOREGROUND} />
          <Text style={specCardStyles.linkText}>{isRTL ? 'عرض التفاصيل' : 'View Details'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function TeacherSpecializations({ specializations, onRemoveSpecialization, onAssign, readOnly, entityName }: { specializations: StudentSpecialization[]; onRemoveSpecialization?: (specId: number) => void; onAssign?: () => void; readOnly?: boolean; entityName?: string }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<ScreenNavProp>();
  const [confirmSpecId, setConfirmSpecId] = useState<number | null>(null);
  const confirmSpec = specializations.find(s => s.id === confirmSpecId);

  return (
    <View style={[sectionStyles.container, createShadow(0, 1, 2, '#000', 0.1)]}>
      <View style={sectionStyles.header}>
        <TouchableOpacity
          style={[sectionStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1)]}
          onPress={onAssign}
        >
          <Text style={sectionStyles.addBtnText}>
            {isRTL ? 'إلحاق في موهبة' : 'Assign to Specialization'}
          </Text>
          <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
        </TouchableOpacity>
        <Text style={sectionStyles.sectionTitle}>
          {isRTL ? 'المواهب' : 'Specializations'}
        </Text>
      </View>
      {specializations.length === 0 ? (
        <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
          {isRTL ? 'لا توجد مواهب مسجلة' : 'No specializations assigned'}
        </Text>
      ) : (
        <View style={[sectionStyles.cardsRow, isRTL && { flexDirection: 'row-reverse' }]}>
          {specializations.map((spec) => (
            <SpecializationCard
              key={spec.id}
              spec={spec}
              readOnly={readOnly}
              onView={() => navigation.navigate('SpecializationDetail', { id: spec.id, name: spec.title, entityName })}
              onRemove={() => setConfirmSpecId(spec.id)}
            />
          ))}
          <View style={sectionStyles.cardSpacer} />
          <View style={sectionStyles.cardSpacer} />
          <View style={sectionStyles.cardSpacer} />
        </View>
      )}

      {/* Confirmation Modal */}
      <Modal visible={confirmSpecId !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setConfirmSpecId(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>
              {isRTL ? 'إزالة الموهبة' : 'Remove Specialization'}
            </Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة المنسق من موهبة "${confirmSpec?.title || ''}"؟\nسيتم إلغاء تعيين المنسق من هذه الموهبة.`
                : `Are you sure you want to remove the teacher from "${confirmSpec?.title || ''}" specialization?\nThe teacher will be unassigned from this specialization.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity
                style={modalStyles.cancelBtn}
                onPress={() => setConfirmSpecId(null)}
              >
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={modalStyles.confirmBtn}
                onPress={() => {
                  if (confirmSpecId !== null) {
                    onRemoveSpecialization?.(confirmSpecId);
                  }
                  setConfirmSpecId(null);
                }}
              >
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

    </View>
  );
}

/* ── Programs Section ────────────────────────────────────────── */
function ProgramCardInline({ program, onView, onRemove, removeLabel, readOnly }: { program: SpecializationProgram; onView?: () => void; onRemove?: () => void; removeLabel?: string; readOnly?: boolean }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View style={progCardStyles.container}>
      <Text style={progCardStyles.title}>{program.name}</Text>
      <View style={progCardStyles.progressRow}>
        <View style={progCardStyles.pctPill}>
          <Text style={progCardStyles.pctText}>{program.progress}</Text>
          <Text style={progCardStyles.pctText}>%</Text>
        </View>
        <View style={progCardStyles.trackBg}>
          <View style={[progCardStyles.trackFill, { width: `${Math.min(program.progress, 100)}%` }]} />
          <View style={[progCardStyles.progressDot, { right: `${Math.min(program.progress, 100)}%` }]} />
        </View>
      </View>
      <View style={progCardStyles.footerRow}>
        {!readOnly && (
          <View>
            <TouchableOpacity onPress={() => setMenuOpen(!menuOpen)}>
              <Ionicons name="ellipsis-vertical" size={18} color={FOREGROUND} />
            </TouchableOpacity>
            {menuOpen && onRemove && (
              <View style={specCardStyles.dropdownMenu}>
                <TouchableOpacity
                  style={specCardStyles.dropdownItem}
                  onPress={() => { setMenuOpen(false); onRemove(); }}
                >
                  <Text style={specCardStyles.dropdownItemText}>
                    {removeLabel || (isRTL ? 'إزالة' : 'Remove')}
                  </Text>
                  <Ionicons name="close-circle-outline" size={16} color="#DC2626" />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={progCardStyles.linkRow} onPress={onView}>
          <Ionicons name="chevron-back" size={14} color={FOREGROUND} />
          <Text style={progCardStyles.linkText}>{isRTL ? 'عرض التفاصيل' : 'View Details'}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function TeacherPrograms({ programs, onRemoveProgram, onAssign, readOnly, entityName }: { programs: SpecializationProgram[]; onRemoveProgram?: (prog: SpecializationProgram) => void; onAssign?: () => void; readOnly?: boolean; entityName?: string }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<ScreenNavProp>();
  const [confirmProg, setConfirmProg] = useState<SpecializationProgram | null>(null);

  return (
    <View style={[sectionStyles.container, createShadow(0, 1, 2, '#000', 0.1)]}>
      <View style={sectionStyles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[sectionStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1)]}
            onPress={onAssign}
          >
            <Text style={sectionStyles.addBtnText}>
              {isRTL ? 'إلحاق في مشروع' : 'Assign to Project'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={sectionStyles.sectionTitle}>
          {isRTL ? 'البرامج والمشاريع' : 'Programs & Projects'}
        </Text>
      </View>
      {programs.length === 0 ? (
        <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
          {isRTL ? 'لا توجد مشاريع مسجلة' : 'No projects assigned'}
        </Text>
      ) : (
        <View style={[sectionStyles.cardsRow, isRTL && { flexDirection: 'row-reverse' }]}>
          {programs.map((prog) => (
            <ProgramCardInline
              key={prog.id}
              program={prog}
              readOnly={readOnly}
              onView={() => navigation.navigate('ProjectDetail', { id: prog.id, name: prog.name, entityName })}
              onRemove={() => setConfirmProg(prog)}
              removeLabel={isRTL ? 'إزالة المشروع' : 'Remove Project'}
            />
          ))}
          <View style={sectionStyles.cardSpacer} />
          <View style={sectionStyles.cardSpacer} />
          <View style={sectionStyles.cardSpacer} />
        </View>
      )}

      <Modal visible={confirmProg !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setConfirmProg(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>
              {isRTL ? 'إزالة المشروع' : 'Remove Project'}
            </Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة المنسق من مشروع "${confirmProg?.name || ''}"؟\nسيتم إلغاء تعيين المنسق من هذا المشروع.`
                : `Are you sure you want to remove the teacher from "${confirmProg?.name || ''}" project?\nThe teacher will be unassigned from this project.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setConfirmProg(null)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={modalStyles.confirmBtn}
                onPress={() => { if (confirmProg) onRemoveProgram?.(confirmProg); setConfirmProg(null); }}
              >
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

    </View>
  );
}

/* ── Activities Section ──────────────────────────────────────── */
function TeacherActivities({ activities, onRemoveActivity, onAssign, readOnly, entityName }: { activities: TalentActivityData[]; onRemoveActivity?: (act: TalentActivityData) => void; onAssign?: () => void; readOnly?: boolean; entityName?: string }) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<ScreenNavProp>();
  const [confirmAct, setConfirmAct] = useState<TalentActivityData | null>(null);

  return (
    <View style={[sectionStyles.container, createShadow(0, 1, 2, '#000', 0.1)]}>
      <View style={sectionStyles.header}>
        {!readOnly && (
          <TouchableOpacity
            style={[sectionStyles.addBtn, createShadow(0, 1, 3, '#000', 0.1)]}
            onPress={onAssign}
          >
            <Text style={sectionStyles.addBtnText}>
              {isRTL ? 'إلحاق في نشاط' : 'Assign to Activity'}
            </Text>
            <Ionicons name="add-circle-outline" size={14} color={FOREGROUND} />
          </TouchableOpacity>
        )}
        <Text style={sectionStyles.sectionTitle}>
          {isRTL ? 'الأنشطة' : 'Activities'}
        </Text>
      </View>
      {activities.length === 0 ? (
        <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
          {isRTL ? 'لا توجد أنشطة مسجلة' : 'No activities assigned'}
        </Text>
      ) : (
        <View style={[sectionStyles.cardsRow, isRTL && { flexDirection: 'row-reverse' }]}>
          {activities.map((act) => (
            <ProgramCardInline
              key={act.id}
              program={{ id: act.id, name: act.name, progress: act.progress }}
              readOnly={readOnly}
              onView={() => navigation.navigate('TalentActivityDetail', { id: act.id, entityName })}
              onRemove={() => setConfirmAct(act)}
              removeLabel={isRTL ? 'إزالة النشاط' : 'Remove Activity'}
            />
          ))}
          <View style={sectionStyles.cardSpacer} />
          <View style={sectionStyles.cardSpacer} />
          <View style={sectionStyles.cardSpacer} />
        </View>
      )}

      <Modal visible={confirmAct !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setConfirmAct(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>
              {isRTL ? 'إزالة النشاط' : 'Remove Activity'}
            </Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة المنسق من نشاط "${confirmAct?.name || ''}"؟\nسيتم إلغاء تعيين المنسق من هذا النشاط.`
                : `Are you sure you want to remove the teacher from "${confirmAct?.name || ''}" activity?\nThe teacher will be unassigned from this activity.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setConfirmAct(null)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={modalStyles.confirmBtn}
                onPress={() => { if (confirmAct) onRemoveActivity?.(confirmAct); setConfirmAct(null); }}
              >
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

    </View>
  );
}

/* ── Main Screen ─────────────────────────────────────────────── */
export default function TeacherDetailScreen() {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<ScreenNavProp>();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const { user } = useAuth();
  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';

  // Detect self-profile mode (coordinator viewing their own profile)
  const isMyProfile = route.name === 'CoordinatorProfile';
  const [resolvedTeacherId, setResolvedTeacherId] = useState<number | undefined>();
  const teacherId = isMyProfile ? resolvedTeacherId : (route.params as any)?.id;
  const entityName = isMyProfile ? undefined : (route.params as any)?.entityName;
  const readOnly = isMyProfile || !!entityName || isStrategyTeam;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TeacherDetailData>({
    id: 0, name: '', school: '', specialization: '',
    eid: '', email: '', phone: '', birthDate: '', gender: '', nationality: '',
    projectCount: 0, activityCount: 0,
    specializations: [], programs: [], activities: [],
  });
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [assignType, setAssignType] = useState<'specialization' | 'project' | 'activity' | null>(null);
  const [assignOptions, setAssignOptions] = useState<{ id: number; name: string }[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchTeacherData = useCallback(async () => {
    setIsLoading(true);
    let effectiveTeacherId = teacherId;
    try {
        // Fetch teacher basic info
        let resp: any = null;
        try {
          resp = isMyProfile
            ? await talentTeachersApi.myProfile()
            : await talentTeachersApi.get(teacherId);
        } catch (profileErr: any) {
          // No teacher record — fall back to SSO user info for self-profile
          if (isMyProfile && profileErr?.response?.status === 404) {
            const transformed: TeacherDetailData = {
              id: 0,
              name: user?.profile?.full_name || user?.profile?.username || '',
              school: '',
              specialization: '',
              eid: '',
              email: user?.profile?.email || '',
              phone: user?.profile?.phone_number || '',
              birthDate: '',
              gender: '',
              nationality: '',
              projectCount: 0,
              activityCount: 0,
              specializations: [],
              programs: [],
              activities: [],
            };
            setData(transformed);
            setIsLoading(false);
            return;
          }
          throw profileErr;
        }
        if (isMyProfile && resp?.id) {
          setResolvedTeacherId(resp.id);
          effectiveTeacherId = resp.id;
        }
        if (resp) {
          // Fetch project links to get project details
          let projectLinks: any[] = [];
          try {
            const linksResp = await talentTeacherProjectLinksApi.list({ teacher: effectiveTeacherId });
            projectLinks = linksResp?.results || linksResp || [];
          } catch (e) {
            console.log('[TeacherDetail] project links failed:', e);
          }

          // Fetch activities for the teacher's projects
          let activities: TalentActivityData[] = [];
          for (const link of projectLinks) {
            try {
              const projResp = await (await import('../../services/apiService')).talentProjectsApi.get(link.project);
              if (projResp?.activities) {
                activities.push(...projResp.activities.map((a: any) => ({
                  id: a.id,
                  name: a.name || '',
                  progress: a.progress_percentage ?? 0,
                  projectId: link.project,
                  linkId: link.id,
                })));
              }
            } catch (_) { /* skip */ }
          }

          const transformed: TeacherDetailData = {
            id: resp.id,
            name: resp.name || '',
            school: resp.school || '',
            specialization: resp.specialization_name || '',
            eid: resp.eid || '',
            email: resp.email || '',
            phone: resp.phone || '',
            birthDate: resp.birth_date || '',
            gender: resp.gender || '',
            nationality: resp.nationality || '',
            projectCount: projectLinks.length,
            activityCount: activities.length,
            specializations: resp.specialization_name ? [{
              id: resp.specialization || 0,
              title: resp.specialization_name,
              progress: 0,
              kpiCount: 0,
              activityCount: activities.length,
              participantCount: 0,
            }] : [],
            programs: projectLinks.map((l: any) => ({
              id: l.project,
              name: l.project_name || '',
              progress: 0,
              linkId: l.id,
            })),
            activities,
          };
          setData(transformed);
        }
      } catch (e) {
        console.log('[TeacherDetail] API failed:', e);
      }

      // Fetch calendar events for the teacher
      try {
        const events = await talentActivitiesApi.calendar({ teacher: effectiveTeacherId });
        setCalendarEvents(Array.isArray(events) ? events : []);
      } catch (e) {
        console.log('[TeacherDetail] Calendar API failed:', e);
        setCalendarEvents([]);
      }

      setIsLoading(false);
  }, [teacherId, isMyProfile]);

  useFocusEffect(
    useCallback(() => {
      fetchTeacherData();
    }, [fetchTeacherData])
  );

  const handleRemoveSpecialization = async (_specId: number) => {
    try {
      await talentTeachersApi.removeSpecialization(teacherId);
      setToastType('success');
      setToastMsg(isRTL ? 'تم إزالة الموهبة بنجاح' : 'Specialization removed successfully');
      fetchTeacherData();
    } catch (e) {
      console.log('[TeacherDetail] remove specialization failed:', e);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في إزالة الموهبة' : 'Failed to remove specialization');
    }
  };

  const handleRemoveProgram = async (prog: SpecializationProgram) => {
    try {
      await talentTeachersApi.removeProject(teacherId, prog.id);
      setToastType('success');
      setToastMsg(isRTL ? 'تم إزالة المشروع بنجاح' : 'Project removed successfully');
      fetchTeacherData();
    } catch (e) {
      console.log('[TeacherDetail] remove program failed:', e);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في إزالة المشروع' : 'Failed to remove project');
    }
  };

  const handleRemoveActivity = async (act: TalentActivityData) => {
    try {
      await talentActivitiesApi.delete(act.id);
      setToastType('success');
      setToastMsg(isRTL ? 'تم إزالة النشاط بنجاح' : 'Activity removed successfully');
      fetchTeacherData();
    } catch (e) {
      console.log('[TeacherDetail] remove activity failed:', e);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في إزالة النشاط' : 'Failed to remove activity');
    }
  };

  const handleOpenEdit = () => {
    navigation.navigate('EditTeacher', { id: teacherId });
  };

  const handleDeleteTeacher = async () => {
    try {
      await talentTeachersApi.delete(teacherId);
      setShowRemoveConfirm(false);
      navigation.goBack();
    } catch (e) {
      console.log('[TeacherDetail] delete failed:', e);
      setShowRemoveConfirm(false);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في إزالة المنسق' : 'Failed to remove teacher');
    }
  };

  const openAssignModal = async (type: 'specialization' | 'project' | 'activity') => {
    setAssignType(type);
    setAssignLoading(true);
    setAssignOptions([]);
    try {
      if (type === 'specialization') {
        const resp = await talentSpecializationsApi.list();
        const items = resp?.results || resp || [];
        setAssignOptions(items.map((s: any) => ({ id: s.id, name: s.name })));
      } else if (type === 'project') {
        const resp = await talentProjectsApi.list();
        const items = resp?.results || resp || [];
        setAssignOptions(items.map((p: any) => ({ id: p.id, name: p.name })));
      } else {
        const resp = await talentActivitiesApi.list();
        const items = resp?.results || resp || [];
        setAssignOptions(items.map((a: any) => ({ id: a.id, name: a.name })));
      }
    } catch (e) {
      console.log('[TeacherDetail] fetch assign options failed:', e);
    }
    setAssignLoading(false);
  };

  const handleAssign = async (selectedId: number) => {
    try {
      if (assignType === 'specialization') {
        await talentTeachersApi.update(teacherId, { specialization: selectedId });
        setToastType('success');
        setToastMsg(isRTL ? 'تم إلحاق الموهبة بنجاح' : 'Specialization assigned successfully');
      } else if (assignType === 'project') {
        await talentTeacherProjectLinksApi.create({ teacher: teacherId, project: selectedId });
        setToastType('success');
        setToastMsg(isRTL ? 'تم إلحاق المشروع بنجاح' : 'Project assigned successfully');
      } else if (assignType === 'activity') {
        // Find the activity's project and link the teacher to it
        const actResp = await talentActivitiesApi.get(selectedId);
        if (actResp?.project) {
          await talentTeacherProjectLinksApi.create({ teacher: teacherId, project: actResp.project });
        }
        setToastType('success');
        setToastMsg(isRTL ? 'تم إلحاق النشاط بنجاح' : 'Activity assigned successfully');
      }
      setAssignType(null);
      fetchTeacherData();
    } catch (e: any) {
      console.log('[TeacherDetail] assign failed:', e);
      const detail = e?.response?.data?.detail || e?.response?.data?.non_field_errors?.[0] || '';
      if (detail.includes('unique') || detail.includes('already')) {
        setToastType('error');
        setToastMsg(isRTL ? 'المنسق مسجل بالفعل' : 'Teacher already assigned');
      } else {
        setToastType('error');
        setToastMsg(isRTL ? 'فشل في الإلحاق' : 'Failed to assign');
      }
      setAssignType(null);
    }
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={isMyProfile ? [
              { label: t('navigation.home') },
              { label: isRTL ? 'الملف الشخصي' : 'Profile' },
            ] : [
              { label: t('navigation.home') },
              { label: t('navigation.talents'), route: 'Talents' },
              ...(entityName
                ? [{ label: entityName, route: 'SpecializationDetail', params: { name: entityName } }]
                : []),
              { label: isRTL ? 'تفاصيل المنسق' : 'Teacher Details' },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              {/* Read-only notice on the coordinator's own profile — this is an
                  informational overview of their assignments, not editable. */}
              {isMyProfile && (
                <View style={styles.readOnlyBanner}>
                  <Ionicons name="eye-outline" size={16} color="#64748B" />
                  <Text style={styles.readOnlyBannerText}>
                    {isRTL
                      ? 'هذه الصفحة للاطلاع فقط — تعرض معلوماتك والتخصصات والمشاريع والأنشطة المسندة إليك'
                      : 'This page is read-only — it shows your information and the specializations, projects and activities assigned to you'}
                  </Text>
                </View>
              )}

              {/* Section 1: Teacher Header */}
              <TeacherHeader data={data} onEdit={handleOpenEdit} onRemove={() => setShowRemoveConfirm(true)} readOnly={readOnly} entityName={entityName} />

              {/* Section 2: Info Cards */}
              <InfoCards data={data} />

              {/* Section 3: Specializations */}
              <TeacherSpecializations specializations={data.specializations} onRemoveSpecialization={handleRemoveSpecialization} onAssign={() => openAssignModal('specialization')} readOnly={readOnly} entityName={entityName} />

              {/* Section 4: Programs & Projects */}
              <TeacherPrograms programs={data.programs} onRemoveProgram={handleRemoveProgram} onAssign={() => openAssignModal('project')} readOnly={readOnly} entityName={entityName} />

              {/* Section 5: Activities */}
              <TeacherActivities activities={data.activities} onRemoveActivity={handleRemoveActivity} onAssign={() => openAssignModal('activity')} readOnly={readOnly} entityName={entityName} />

              {/* Section 6: Calendar */}
              <ActivityCalendar
                events={calendarEvents}
                hideNewButton
                onEventPress={(event) => navigation.navigate('TalentActivityDetail', { id: event.id, entityName })}
              />
            </View>
          )}
        </View>
      </ScrollView>
      {/* Remove Teacher Confirmation */}
      <Modal visible={showRemoveConfirm} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setShowRemoveConfirm(false)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>
              {isRTL ? 'إزالة المنسق' : 'Remove Teacher'}
            </Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من إزالة "${data.name}" من قائمة المنسقين؟\nلا يمكن التراجع عن هذا الإجراء.`
                : `Are you sure you want to remove "${data.name}" from the teachers list?\nThis action cannot be undone.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setShowRemoveConfirm(false)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={modalStyles.confirmBtn} onPress={handleDeleteTeacher}>
                <Text style={modalStyles.confirmBtnText}>{isRTL ? 'إزالة' : 'Remove'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Assign Modal */}
      <Modal visible={assignType !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setAssignType(null)}>
          <Pressable style={assignModalContentStyle} onPress={(e) => e.stopPropagation()}>
            <Text style={assignModalTitleStyle}>
              {assignType === 'specialization' ? (isRTL ? 'إلحاق في موهبة' : 'Assign to Specialization')
                : assignType === 'project' ? (isRTL ? 'إلحاق في مشروع' : 'Assign to Project')
                : (isRTL ? 'إلحاق في نشاط' : 'Assign to Activity')}
            </Text>
            {assignLoading ? (
              <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
                {isRTL ? 'جاري التحميل...' : 'Loading...'}
              </Text>
            ) : assignOptions.length === 0 ? (
              <Text style={{ textAlign: 'center', color: '#94A3B8', fontFamily: FONTS.regular, fontSize: 14, paddingVertical: 20 }}>
                {isRTL ? 'لا توجد خيارات متاحة' : 'No options available'}
              </Text>
            ) : (
              <ScrollView style={{ maxHeight: 300 }}>
                {assignOptions.map((opt) => (
                  <TouchableOpacity
                    key={opt.id}
                    style={assignModalStyles.optionRow}
                    onPress={() => handleAssign(opt.id)}
                  >
                    <Text style={assignModalStyles.optionText}>{opt.name}</Text>
                    <Ionicons name="add-circle-outline" size={18} color="#22C55E" />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
            <View style={{ marginTop: 16 }}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setAssignType(null)}>
                <Text style={modalStyles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </PageContainer>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                       */
/* ══════════════════════════════════════════════════════════════ */

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  container: { flex: 1 },
  sections: { gap: 20 },
  readOnlyBanner: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F1F5F9',
    borderColor: '#E2E8F0',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  readOnlyBannerText: {
    flex: 1,
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
  },
});

const headerStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF', borderRadius: 8,
    borderWidth: 1, borderColor: '#E2E8F0',
    paddingHorizontal: 16, paddingVertical: 12,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 20 },
  actionsRow: { flexDirection: 'row', alignItems: 'center', gap: 20 },
  actionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8,
    paddingHorizontal: 12, paddingVertical: 6, minHeight: 32,
  },
  actionText: { fontSize: 14, fontFamily: FONTS.regular, color: FOREGROUND },
  nameCol: { flex: 1, alignItems: 'flex-end', gap: 8 },
  name: { fontSize: 20, fontFamily: FONTS.bold || FONTS.medium, color: FOREGROUND, textAlign: 'right' },
  subtitle: { fontSize: 16, fontFamily: FONTS.regular, color: '#64748B', textAlign: 'right' },
  entityTag: { backgroundColor: '#A6813F', borderRadius: 40, paddingHorizontal: 12, paddingVertical: 4, alignSelf: 'flex-end' as const },
  entityTagText: { fontSize: 12, fontFamily: FONTS.regular, color: '#FFFFFF' },
});

const infoStyles = StyleSheet.create({
  cardsRow: { flexDirection: 'row', gap: 20, flexWrap: 'wrap' },
  card: {
    flex: 1, minWidth: 280, backgroundColor: '#FFFFFF',
    borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0',
    padding: 12, gap: 16,
  },
  cardTitle: {
    fontSize: 14, fontFamily: FONTS.regular, color: FOREGROUND,
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    justifyContent: 'center',
  },
  labelPill: {
    backgroundColor: 'rgba(51,65,85,0.1)', borderRadius: 4,
    width: 120, paddingHorizontal: 4, paddingVertical: 2,
    alignItems: 'flex-end', justifyContent: 'center',
  },
  labelText: {
    fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND,
    textAlign: 'right',
  },
  valueText: {
    flex: 1, fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND,
    textAlign: 'right',
  },
});

const specCardStyles = StyleSheet.create({
  container: {
    flex: 1, minWidth: 240, backgroundColor: '#F8FAFC',
    borderRadius: 10, borderWidth: 1, borderColor: '#E2E8F0',
    padding: 14, gap: 12,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  nameCol: { flex: 1, alignItems: 'flex-end' },
  name: { fontSize: 14, fontFamily: FONTS.medium, color: FOREGROUND, textAlign: 'right' },
  statsRow: { flexDirection: 'row', justifyContent: 'space-around', gap: 8 },
  statItem: { alignItems: 'center', gap: 2 },
  statLabel: { fontSize: 11, fontFamily: FONTS.regular, color: '#64748B' },
  statValue: { fontSize: 16, fontFamily: FONTS.medium, color: FOREGROUND },
  footerRow: { flexDirection: 'row', alignItems: 'center', paddingTop: 4 },
  linkRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  linkText: { fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND },
  dropdownMenu: {
    position: 'absolute', bottom: 24, left: 0, zIndex: 10,
    backgroundColor: '#FFFFFF', borderRadius: 8, borderWidth: 1, borderColor: '#E2E8F0',
    minWidth: 160, paddingVertical: 4,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15, shadowRadius: 6,
    elevation: 6,
  },
  dropdownItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
    paddingHorizontal: 12, paddingVertical: 10,
  },
  dropdownItemText: { fontSize: 13, fontFamily: FONTS.regular, color: '#DC2626' },
});

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center', alignItems: 'center',
  },
  content: {
    backgroundColor: '#FFFFFF', borderRadius: 16, padding: 24,
    width: 400, maxWidth: '90%',
  },
  title: {
    fontSize: 18, fontFamily: FONTS.medium, color: FOREGROUND,
    textAlign: 'center', marginBottom: 8,
  },
  message: {
    fontSize: 14, fontFamily: FONTS.regular, color: '#64748B',
    textAlign: 'center', lineHeight: 22, marginBottom: 20,
  },
  btnRow: { flexDirection: 'row', gap: 12, justifyContent: 'center' },
  cancelBtn: {
    flex: 1, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  cancelBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: FOREGROUND },
  confirmBtn: {
    flex: 1, backgroundColor: '#DC2626', borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  confirmBtnText: { fontSize: 14, fontFamily: FONTS.medium, color: '#FFFFFF' },
});


const assignModalContentStyle: any = {
  backgroundColor: '#FFFFFF', borderRadius: 16, padding: 24,
  width: 480, maxWidth: '90%',
};
const assignModalTitleStyle: any = {
  fontSize: 18, fontFamily: FONTS.medium, color: FOREGROUND,
  textAlign: 'right', marginBottom: 16,
};

const assignModalStyles = StyleSheet.create({
  optionRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 12, paddingHorizontal: 8,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#F1F5F9',
  },
  optionText: { fontSize: 14, fontFamily: FONTS.regular, color: FOREGROUND, textAlign: 'right', flex: 1 },
});

const sectionStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF', borderRadius: 12,
    borderWidth: 1, borderColor: '#E2E8F0', padding: 16, gap: 16,
  },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { fontSize: 16, fontFamily: FONTS.medium, color: FOREGROUND, textAlign: 'right' },
  addBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8,
    paddingHorizontal: 10, paddingVertical: 5, minHeight: 30,
  },
  addBtnText: { fontSize: 13, fontFamily: FONTS.regular, color: FOREGROUND },
  cardsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  cardSpacer: { flex: 1, minWidth: 240 },
});

const progCardStyles = StyleSheet.create({
  container: {
    flex: 1, minWidth: 240, backgroundColor: '#F8FAFC',
    borderRadius: 10, borderWidth: 1, borderColor: '#E2E8F0',
    padding: 14, gap: 10,
  },
  title: { fontSize: 14, fontFamily: FONTS.medium, color: FOREGROUND, textAlign: 'right' },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  pctPill: {
    flexDirection: 'row', alignItems: 'center', gap: 2,
    backgroundColor: '#22C55E', borderRadius: 12,
    paddingHorizontal: 8, paddingVertical: 2, minWidth: 44,
    justifyContent: 'center',
  },
  pctText: { fontSize: 12, fontFamily: FONTS.medium, color: '#FFFFFF' },
  trackBg: {
    flex: 1, height: 8, backgroundColor: '#E2E8F0',
    borderRadius: 4, overflow: 'hidden', position: 'relative',
  },
  trackFill: {
    position: 'absolute', left: 0, top: 0, bottom: 0,
    backgroundColor: '#22C55E', borderRadius: 4,
  },
  progressDot: {
    position: 'absolute', top: -2, width: 12, height: 12,
    borderRadius: 6, backgroundColor: '#22C55E', borderWidth: 2, borderColor: '#FFFFFF',
  },
  footerRow: { flexDirection: 'row', alignItems: 'center', paddingTop: 2 },
  linkRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  linkText: { fontSize: 12, fontFamily: FONTS.regular, color: FOREGROUND },
});
